export const degreesToDirection = (degrees) => {
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round((degrees || 0) / 22.5) % 16;
  return directions[index];
};

export const angleDiff = (a = 0, b = 0) => {
  const diff = Math.abs((((a - b) % 360) + 360) % 360);
  return diff > 180 ? 360 - diff : diff;
};

export const getWindCategory = (degrees) => {
  const normalized = ((degrees % 360) + 360) % 360;

  if (normalized >= 225 && normalized <= 315) {
    return 'onshore';
  }

  if (normalized >= 45 && normalized <= 135) {
    return 'offshore';
  }

  return 'cross-shore';
};

export const getWindCategoryLabel = (category, language) => {
  const labels = {
    english: {
      'onshore': 'Onshore',
      'offshore': 'Offshore',
      'cross-shore': 'Cross-shore',
    },
    swedish: {
      'onshore': 'Pålandsvind',
      'offshore': 'Frånlandsvind',
      'cross-shore': 'Sidvind',
    },
  };

  return labels[language]?.[category] || category;
};

export const formatCoordinates = () => `57°04'51.2"N 12°15'27.7"E`;

export const formatForecastDate = (dateString, language) => {
  const locale = language === 'swedish' ? 'sv-SE' : 'en-GB';

  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(dateString));
};

export const formatHourLabel = (dateTimeString, language) => {
  const locale = language === 'swedish' ? 'sv-SE' : 'en-GB';

  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(dateTimeString));
};


export function getWeatherIconPath(weatherCode, isDay = true) {
  // Open-Meteo weather codes
  // https://open-meteo.com/en/docs

  if (weatherCode === 0) {
    return isDay
      ? `${process.env.PUBLIC_URL}/static/icons/weather/01_sun.png`
      : `${process.env.PUBLIC_URL}/static/icons/weather/11_clear_night.png`;
  }

  if ([1, 2].includes(weatherCode)) {
    return isDay
      ? `${process.env.PUBLIC_URL}/static/icons/weather/02_partly_cloudy.png`
      : `${process.env.PUBLIC_URL}/static/icons/weather/12_partly_cloudy_night.png`;
  }

  if (weatherCode === 3) {
    return `${process.env.PUBLIC_URL}/static/icons/weather/03_cloudy.png`;
  }

  if ([45, 48].includes(weatherCode)) {
    return `${process.env.PUBLIC_URL}/static/icons/weather/09_fog.png`;
  }

  if ([51, 53, 55].includes(weatherCode)) {
    return `${process.env.PUBLIC_URL}/static/icons/weather/05_rain.png`;
  }

  if ([56, 57].includes(weatherCode)) {
    return `${process.env.PUBLIC_URL}/static/icons/weather/08_sleet.png`;
  }

  if ([61, 63].includes(weatherCode)) {
    return `${process.env.PUBLIC_URL}/static/icons/weather/05_rain.png`;
  }

  if ([65, 66, 67].includes(weatherCode)) {
    return `${process.env.PUBLIC_URL}/static/icons/weather/06_heavy_rain.png`;
  }

  if ([71, 73, 75].includes(weatherCode)) {
    return `${process.env.PUBLIC_URL}/static/icons/weather/07_snow.png`;
  }

  if (weatherCode === 77) {
    return `${process.env.PUBLIC_URL}/static/icons/weather/14_snow.png`;
  }

  if ([80, 81].includes(weatherCode)) {
    return `${process.env.PUBLIC_URL}/static/icons/weather/16_partly_cloudy_rain.png`;
  }

  if (weatherCode === 82) {
    return `${process.env.PUBLIC_URL}/static/icons/weather/06_heavy_rain.png`;
  }

  if ([85, 86].includes(weatherCode)) {
    return `${process.env.PUBLIC_URL}/static/icons/weather/17_partly_cloudy_snow.png`;
  }

  if ([95, 96, 99].includes(weatherCode)) {
    return `${process.env.PUBLIC_URL}/static/icons/weather/04_thunderstorm.png`;
  }

  return isDay
    ? `${process.env.PUBLIC_URL}/static/icons/weather/02_partly_cloudy.png`
    : `${process.env.PUBLIC_URL}/static/icons/weather/12_partly_cloudy_night.png`;
}

export function getWeatherLabel(weatherCode) {
  if (weatherCode === 0) return "Clear";
  if ([1, 2].includes(weatherCode)) return "Partly cloudy";
  if (weatherCode === 3) return "Cloudy";
  if ([45, 48].includes(weatherCode)) return "Fog";
  if ([51, 53, 55].includes(weatherCode)) return "Drizzle";
  if ([56, 57].includes(weatherCode)) return "Freezing drizzle";
  if ([61, 63].includes(weatherCode)) return "Rain";
  if ([65, 66, 67].includes(weatherCode)) return "Heavy rain";
  if ([71, 73, 75, 77].includes(weatherCode)) return "Snow";
  if ([80, 81, 82].includes(weatherCode)) return "Rain showers";
  if ([85, 86].includes(weatherCode)) return "Snow showers";
  if ([95, 96, 99].includes(weatherCode)) return "Thunderstorm";
  return "Unknown";
}

export const buildForecastDays = (currentWeather) => {
  const daily = currentWeather?.daily;

  if (!daily?.time?.length) {
    return [];
  }

  return daily.time.map((time, index) => ({
    time,
    temperatureMax: daily.temperature_2m_max?.[index],
    temperatureMin: daily.temperature_2m_min?.[index],
    windSpeedMax: daily.wind_speed_10m_max?.[index],
    windDirectionDominant: daily.wind_direction_10m_dominant?.[index],
    weatherCode: daily.weather_code?.[index],
  }));
};

export const buildHourlyConditionsForDate = (spotData, selectedDate) => {
  const hourlyWeather = spotData?.weather?.hourly;
  const hourlyMarine = spotData?.marine?.hourly;

  if (!selectedDate || !hourlyWeather?.time?.length) {
    return [];
  }

  const marineIndexByTime = new Map((hourlyMarine?.time || []).map((time, index) => [time, index]));

  const entries = hourlyWeather.time
    .map((time, index) => {
      const marineIndex = marineIndexByTime.get(time);

      return {
        time,
        windSpeed: hourlyWeather.wind_speed_10m?.[index],
        windDirection: hourlyWeather.wind_direction_10m?.[index],
        temperature: hourlyWeather.temperature_2m?.[index],
        weatherCode: hourlyWeather.weather_code?.[index],
        isDay: hourlyWeather.is_day?.[index],
        waveHeight: marineIndex != null ? hourlyMarine.wave_height?.[marineIndex] : null,
        waveDirection: marineIndex != null ? hourlyMarine.wave_direction?.[marineIndex] : null,
        wavePeriod: marineIndex != null ? hourlyMarine.wave_period?.[marineIndex] : null,
        swellHeight: marineIndex != null ? hourlyMarine.swell_wave_height?.[marineIndex] : null,
        swellDirection: marineIndex != null ? hourlyMarine.swell_wave_direction?.[marineIndex] : null,
        swellPeriod: marineIndex != null ? hourlyMarine.swell_wave_period?.[marineIndex] : null,
        seaSurfaceTemperature: marineIndex != null ? hourlyMarine.sea_surface_temperature?.[marineIndex] : null,
      };
    })
    .filter((entry) => entry.time.startsWith(selectedDate));

  return entries.slice(0, 24);
};

const getSurfRatingLabel = (score, language) => {
  const labels = {
    english: {
      poor: 'Poor',
      maybe: 'Maybe',
      surfable: 'Surfable',
      good: 'Good',
      excellent: 'Excellent',
    },
    swedish: {
      poor: 'Svagt',
      maybe: 'Kanske',
      surfable: 'Surfbar',
      good: 'Bra',
      excellent: 'Excellent',
    },
  };

  const bucket = score <= 24 ? 'poor' : score <= 44 ? 'maybe' : score <= 64 ? 'surfable' : score <= 79 ? 'good' : 'excellent';
  return {
    bucket,
    label: labels[language]?.[bucket] || labels.english[bucket],
  };
};

export const getApelvikenWindScore = (windDir = 0, windSpeed = 0) => {
  const diff = angleDiff(windDir, 45);
  const speed = Number(windSpeed) || 0;

  let directionScore = 0;
  if (diff <= 20) directionScore = 20;
  else if (diff <= 35) directionScore = 16;
  else if (diff <= 55) directionScore = 10;
  else if (diff <= 75) directionScore = 5;
  else directionScore = 0;

  let speedScore = 0;
  if (speed >= 3 && speed <= 7) speedScore = 15;
  else if (speed > 7 && speed <= 10) speedScore = 11;
  else if (speed >= 1 && speed < 3) speedScore = 8;
  else if (speed < 1) speedScore = 5;
  else if (speed > 10 && speed <= 13) speedScore = 6;
  else speedScore = 2;

  if (diff > 75) {
    if (speed >= 5 && speed < 8) speedScore = Math.max(0, speedScore - 5);
    if (speed >= 8) speedScore = 0;
  }

  return directionScore + speedScore;
};

export const getApelvikenWindLabel = (windDir = 0, windSpeed = 0, language = 'english') => {
  const diff = angleDiff(windDir, 45);
  const speed = Number(windSpeed) || 0;

  const key = diff <= 20 && speed >= 3 && speed <= 7
    ? 'excellentOffshore'
    : diff <= 35 && speed >= 2 && speed <= 10
      ? 'goodOffshore'
      : diff <= 55 && speed <= 10
        ? 'manageable'
        : diff <= 75
          ? 'crossAffected'
          : speed >= 6
            ? 'onshorePoor'
            : 'weakNotIdeal';

  const labels = {
    english: {
      excellentOffshore: 'Excellent offshore',
      goodOffshore: 'Good offshore',
      manageable: 'Manageable',
      crossAffected: 'Cross-wind affected',
      onshorePoor: 'Onshore / poor',
      weakNotIdeal: 'Weak but not ideal',
    },
    swedish: {
      excellentOffshore: 'Excellent frånlandsvind',
      goodOffshore: 'Bra frånlandsvind',
      manageable: 'Hanterbart',
      crossAffected: 'Påverkat av sidvind',
      onshorePoor: 'Pålandsvind / svagt',
      weakNotIdeal: 'Svagt men inte idealiskt',
    },
  };

  return labels[language]?.[key] || labels.english[key];
};

const scoreWind = (windDir = 0, windSpeed = 0) => getApelvikenWindScore(windDir, windSpeed);

const scoreSwellDirection = (swellDir = 0) => {
  const diff = angleDiff(swellDir, 292.5);
  if (diff <= 15) return 25;
  if (diff <= 30) return 20;
  if (diff <= 50) return 10;
  return 0;
};

const scoreSwellPeriod = (period = 0) => {
  if (period >= 9) return 20;
  if (period >= 7) return 15;
  if (period >= 5) return 8;
  if (period >= 4) return 4;
  return 0;
};

const scoreWaveHeight = (height = 0) => {
  if (height >= 0.7 && height <= 1.5) return 15;
  if (height >= 0.4 && height < 0.7) return 10;
  if (height > 1.5 && height <= 2.2) return 11;
  if (height >= 0.25) return 5;
  return 0;
};

const scoreSeaTemperature = (seaTemp) => {
  if (seaTemp == null) return 2;
  if (seaTemp >= 12) return 5;
  if (seaTemp >= 8) return 4;
  if (seaTemp >= 5) return 3;
  return 1;
};

export const getSurfAssessment = ({
  windDir,
  windSpeed,
  swellDir,
  swellPeriod,
  waveHeight,
  seaTemperature,
  language = 'english',
}) => {
  const score = Math.max(
    0,
    Math.min(
      100,
      Math.round(
        scoreWind(windDir, windSpeed) +
          scoreSwellDirection(swellDir) +
          scoreSwellPeriod(swellPeriod) +
          scoreWaveHeight(waveHeight) +
          scoreSeaTemperature(seaTemperature)
      )
    )
  );

  const rating = getSurfRatingLabel(score, language);
  return { score, ...rating };
};

export const generateSurfReport = ({
  score,
  windCategory,
  windDirection,
  windSpeed,
  swellDirection,
  swellPeriod,
  waveHeight,
  seaTemperature,
  language = 'english',
}) => {
  const isSwedish = language === 'swedish';
  const { bucket } = getSurfRatingLabel(score, language);
  const swellAlignment = angleDiff(swellDirection ?? 0, 292.5);

  const baseReports = {
    english: {
      poor: 'Poor conditions. Expect weak or messy surf with limited shape and low ride quality.',
      maybe: 'Maybe surfable, but conditions look inconsistent and may only offer short workable windows.',
      surfable: 'Surfable conditions. There should be enough energy and shape for a decent session.',
      good: 'Good surf on offer. Winds and swell are lining up well for Apelviken.',
      excellent: 'Excellent conditions. The setup points to one of the better forecast windows.',
    },
    swedish: {
      poor: 'Svaga förhållanden. Räkna med stökig eller svag surf med begränsad form och kvalitet.',
      maybe: 'Kanske surfbart, men förhållandena ser ojämna ut och kan bara ge korta fönster.',
      surfable: 'Surfbara förhållanden. Det bör finnas tillräckligt med energi och form för ett okej pass.',
      good: 'Bra surf väntar. Vind och dyning ligger relativt rätt för Apelviken.',
      excellent: 'Excellent förhållanden. Prognosen pekar på ett av de bättre surf-fönstren.',
    },
  };

  const apelvikenWindScore = getApelvikenWindScore(windDirection, windSpeed);

  const windSentence = isSwedish
    ? apelvikenWindScore >= 30
      ? 'Ren frånlandsvind bör hjälpa till att hålla vågorna organiserade och ytan ren.'
      : apelvikenWindScore >= 22
        ? 'Frånlandsvinden ser stödjande ut, med chans på ganska rena ytförhållanden.'
        : apelvikenWindScore >= 12
          ? 'Vinden är användbar, men inte perfekt linjerad för Apelviken.'
          : apelvikenWindScore >= 6
            ? 'Sidvind kan ge textur och minska formen på vågorna.'
            : 'Vinden riskerar att göra förhållandena stökiga och mindre definierade.'
    : apelvikenWindScore >= 30
      ? 'Clean offshore wind should help keep the waves organized and the surface tidy.'
      : apelvikenWindScore >= 22
        ? 'Offshore wind looks supportive, with decent surface quality likely.'
        : apelvikenWindScore >= 12
          ? 'Wind is workable, though not perfectly lined up for Apelviken.'
          : apelvikenWindScore >= 6
            ? 'Cross-wind may add texture and reduce shape.'
            : 'Wind is likely to make conditions messy and less defined.';

  const swellDirectionSentence = isSwedish
    ? swellAlignment <= 30
      ? 'Dyningens riktning ser väl anpassad ut för Apelviken.'
      : swellAlignment <= 50
        ? 'Dyningens riktning är delvis användbar, men inte helt idealisk.'
        : 'Dyningens riktning är inte särskilt gynnsam för den här surfplatsen.'
    : swellAlignment <= 30
      ? 'The swell direction looks well aligned for Apelviken.'
      : swellAlignment <= 50
        ? 'The swell direction has some potential, though it is not perfectly lined up.'
        : 'The swell direction is not especially favorable for this spot.';

  const periodSentence = isSwedish
    ? (swellPeriod ?? 0) >= 9
      ? 'Dyningsperioden tyder på mer organiserad energi och bättre form.'
      : (swellPeriod ?? 0) >= 6
        ? 'Dyningsperioden ser användbar ut med okej energi i vågorna.'
        : 'Den korta dyningsperioden kan ge svagare och mindre definierade vågor.'
    : (swellPeriod ?? 0) >= 9
      ? 'The swell period suggests more organized energy and better shape.'
      : (swellPeriod ?? 0) >= 6
        ? 'The swell period looks workable with enough energy for a session.'
        : 'The short swell period may reduce wave quality and definition.';

  const heightSentence = isSwedish
    ? (waveHeight ?? 0) >= 1.6
      ? 'Det finns ordentlig energi i vattnet, men kvaliteten blir mer beroende av vinden.'
      : (waveHeight ?? 0) >= 0.7
        ? 'Våghöjden ser lovande ut för ett roligt pass.'
        : 'Våghöjden ser mindre ut, så räkna med färre tydliga set.'
    : (waveHeight ?? 0) >= 1.6
      ? 'There is solid energy in the water, though quality will depend heavily on the wind.'
      : (waveHeight ?? 0) >= 0.7
        ? 'Wave height looks promising for a fun session.'
        : 'Expect smaller surf with fewer standout waves.';

  const temperatureSentence = seaTemperature == null
    ? null
    : isSwedish
      ? seaTemperature <= 6
        ? 'Kallt vatten kräver full vinterutrustning med huva, handskar och boots.'
        : seaTemperature <= 10
          ? 'Det är fortfarande kyligt i vattnet, så en varm dräkt är rätt val.'
          : 'Vattentemperaturen ser mer hanterbar ut, men rätt våtdräkt behövs fortfarande.'
      : seaTemperature <= 6
        ? 'Cold water means full winter gear with hood, gloves, and boots is the safe call.'
        : seaTemperature <= 10
          ? 'It is still cold in the water, so a warm full suit is the right call.'
          : 'Water temperature looks more manageable, though proper rubber is still recommended.';

  return [
    baseReports[language]?.[bucket] || baseReports.english[bucket],
    windSentence,
    swellDirectionSentence,
    periodSentence,
    heightSentence,
    temperatureSentence,
  ]
    .filter(Boolean)
    .join(' ');
};

export const generalIcons = {
  wind: `${process.env.PUBLIC_URL}/static/icons/general/wind-icon.png`,
  wave: `${process.env.PUBLIC_URL}/static/icons/general/wave-icon.png`,
  waterTemp: `${process.env.PUBLIC_URL}/static/icons/general/water-temp-icon.png`,
  airTemp: `${process.env.PUBLIC_URL}/static/icons/general/air-temp-icon.png`,
  surfScore: `${process.env.PUBLIC_URL}/static/icons/general/surf-score-icon.png`,
  wetsuit: `${process.env.PUBLIC_URL}/static/icons/general/wetsuit-icon.png`,
  spotPin: `${process.env.PUBLIC_URL}/static/icons/general/spot-pin-icon.png`,
  compass: `${process.env.PUBLIC_URL}/static/icons/general/compass-icon.png`,
  tide: `${process.env.PUBLIC_URL}/static/icons/general/tide-icon.png`,
  swellDirection: `${process.env.PUBLIC_URL}/static/icons/general/swell-direction-icon.png`,
};